/**
 * Platform console → Marketing: campaigns, their recipient lists, and the
 * platform-wide send window (/api/admin/platform/marketing).
 */
import { apiFetch } from './client';

export type CampaignStatus = 'draft' | 'sending' | 'paused' | 'done';

export interface MarketingTemplate {
  key: string;
  label: string;
  audience: string;
  subject: string;
  body: string;
}

export interface SendWindow {
  lastHour: number;
  lastDay: number;
  hourlyCap: number;
  dailyCap: number;
}

export interface MarketingCampaign {
  id: number;
  name: string;
  template_key: string;
  subject: string;
  body: string;
  status: CampaignStatus;
  created_at: string;
  updated_at: string;
}

export interface CampaignListRow extends MarketingCampaign {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  /**
   * Tracking (072): recipients whose open pixel loaded at least once, and
   * those whose email came back — hard (the address is gone, and skipped from
   * then on) and soft (a temporary refusal). Absent from a server before 072.
   */
  opened?: number;
  bounced?: number;
  soft_bounced?: number;
}

export interface MarketingRecipient {
  id: number;
  email: string;
  name: string | null;
  sent_at: string | null;
  message_id: string | null;
  failed_at: string | null;
  error: string | null;
  /** Tracking (072): the first time the open pixel loaded, and how many times it has. */
  first_opened_at?: string | null;
  open_count?: number;
  /** Tracking (072): a bounce notice read from the sending mailbox, matched by Message-ID. */
  bounced_at?: string | null;
  bounce_kind?: 'hard' | 'soft' | null;
  bounce_detail?: string | null;
}

export interface CsvUploadResult {
  success: boolean;
  added: number;
  invalid: number;
  duplicatesInFile: number;
  alreadyOnList: number;
}

/** One hard-bounced address campaigns are skipping, and until when. */
export interface Suppression { email: string; reason: string; detail: string | null; created_at: string; expires_at: string }

export const listSuppressionsApi = () =>
  apiFetch<{ success: true; suppressions: Suppression[]; days: number }>('/admin/platform/marketing/suppressions');

export const removeSuppressionApi = (email: string) =>
  apiFetch<{ success: true }>('/admin/platform/marketing/suppressions', { method: 'DELETE', body: JSON.stringify({ email }) });

export const listCampaignsApi = () =>
  apiFetch<{
    success: boolean; campaigns: CampaignListRow[]; window: SendWindow;
    unsubscribed: number; smtpConfigured: boolean; templates: MarketingTemplate[];
    /** Tracking (072): addresses on the suppression list — hard-bounced, skipped by every later send */
    suppressed?: number;
  }>('/admin/platform/marketing');

export const createCampaignApi = (body: { name: string; templateKey: string; subject: string; body: string }) =>
  apiFetch<{ success: boolean; campaign: MarketingCampaign }>('/admin/platform/marketing', {
    method: 'POST', body: JSON.stringify(body),
  });

export const getCampaignApi = (id: number) =>
  apiFetch<{
    success: boolean; campaign: MarketingCampaign; recipients: MarketingRecipient[];
    window: SendWindow; smtpConfigured: boolean;
  }>(`/admin/platform/marketing/${id}`);

export const updateCampaignApi = (id: number, body: { name?: string; subject?: string; body?: string }) =>
  apiFetch<{ success: boolean; campaign: MarketingCampaign }>(`/admin/platform/marketing/${id}`, {
    method: 'PATCH', body: JSON.stringify(body),
  });

export const deleteCampaignApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/platform/marketing/${id}`, { method: 'DELETE' });

export const uploadCampaignCsvApi = (id: number, file: File) => {
  const body = new FormData();
  body.append('file', file);
  return apiFetch<CsvUploadResult>(`/admin/platform/marketing/${id}/recipients`, { method: 'POST', body });
};

export const removeCampaignRecipientApi = (id: number, recipientId: number) =>
  apiFetch<{ success: boolean }>(`/admin/platform/marketing/${id}/recipients/${recipientId}`, { method: 'DELETE' });

export const previewCampaignApi = (id: number) =>
  apiFetch<{ success: boolean; subject: string; html: string; text: string }>(`/admin/platform/marketing/${id}/preview`);

export const testCampaignApi = (id: number, to?: string) =>
  apiFetch<{ success: boolean; to: string }>(`/admin/platform/marketing/${id}/test`, {
    method: 'POST', body: JSON.stringify(to ? { to } : {}),
  });

export const startCampaignApi = (id: number) =>
  apiFetch<{ success: boolean; campaign: MarketingCampaign }>(`/admin/platform/marketing/${id}/start`, { method: 'POST' });

export const pauseCampaignApi = (id: number) =>
  apiFetch<{ success: boolean; campaign: MarketingCampaign }>(`/admin/platform/marketing/${id}/pause`, { method: 'POST' });
